import React, { useState, useEffect } from 'react';
import API from '../utils/api';

// ── Star Picker ──────────────────────────────────────────
function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="star-picker" role="group" aria-label="Rating">
      {[1,2,3,4,5].map(n => (
        <button
          key={n} type="button"
          className={`star-picker__star${(hover || value) >= n ? ' on' : ''}`}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(n)}
          aria-label={`${n} star${n > 1 ? 's' : ''}`}
        >★</button>
      ))}
    </div>
  );
}

// ── Review Card ──────────────────────────────────────────
export function ReviewCard({ review }) {
  const initials = (name) => name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  return (
    <div className="review-card">
      <div className="review-card__stars">
        {[...Array(5)].map((_, i) => (
          <span key={i} className={i < review.rating ? 'star filled' : 'star'}>★</span>
        ))}
      </div>
      <div className="review-card__quote">"</div>
      <p className="review-card__text">{review.text}</p>
      <div className="review-card__author">
        <div className="review-card__avatar">{initials(review.name)}</div>
        <div>
          <span className="review-card__name">{review.name}</span>
          <span className="review-card__role">{review.role || 'Verified Client'}</span>
        </div>
      </div>
    </div>
  );
}

export function ReviewsCarousel({ reviews }) {
  if (!reviews.length) return null;

  const shouldLoop = reviews.length > 1;
  const displayReviews = shouldLoop ? [...reviews, ...reviews] : reviews;

  return (
    <div className="reviews-carousel">
      <div className="reviews-carousel__mask">
        <div className={`reviews-carousel__track ${!shouldLoop ? 'reviews-carousel__track--static' : ''}`}>
          {displayReviews.map((r, i) => (
            <ReviewCard key={`${r._id}-${i}`} review={r} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Review Submit Form ────────────────────────────────────
export default function ReviewForm({ onNewReview }) {
  const [form, setForm]       = useState({ name: '', role: '', rating: 0, text: '' });
  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverErr, setServerErr] = useState('');

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.text.trim()) e.text = 'Review text is required';
    if (!form.rating)      e.rating = 'Please select a rating';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerErr('');
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      const res = await API.post('/reviews', form);
      if (res.data.success) {
        setSubmitted(true);
        if (onNewReview) onNewReview(res.data.data);
        setForm({ name: '', role: '', rating: 0, text: '' });
      }
    } catch (err) {
      setServerErr(err.response?.data?.message || 'Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="review-form__success">
        <span className="review-form__success-icon">🌸</span>
        <h4>Thank you for your review!</h4>
        <p>Your feedback has been added to our testimonials.</p>
        <button onClick={() => setSubmitted(false)} className="btn-clay-sm">Write Another</button>
      </div>
    );
  }

  return (
    <form className="review-form" onSubmit={handleSubmit} noValidate>
      <h3 className="review-form__title">Share Your Experience ✦</h3>
      <p className="review-form__sub">Your review will appear in our testimonials</p>
      {serverErr && <div className="review-form__err-msg">{serverErr}</div>}

      <StarPicker value={form.rating} onChange={v => { setForm(f => ({...f, rating:v})); setErrors(e => ({...e, rating:''})); }} />
      {errors.rating && <span className="field-err">{errors.rating}</span>}

      <div className="review-form__row">
        <div className="review-form__group">
          <label className="field-label">Your Name *</label>
          <input type="text" value={form.name}
            onChange={e => { setForm(f => ({...f, name:e.target.value})); setErrors(er => ({...er, name:''})); }}
            placeholder="e.g. Priya Sharma"
            className={`field-input${errors.name ? ' error' : ''}`} />
          {errors.name && <span className="field-err">{errors.name}</span>}
        </div>
        <div className="review-form__group">
          <label className="field-label">Title / Role</label>
          <input type="text" value={form.role}
            onChange={e => setForm(f => ({...f, role:e.target.value}))}
            placeholder="e.g. Bride, Regular Client"
            className="field-input" />
        </div>
      </div>

      <div className="review-form__group">
        <label className="field-label">Your Review *</label>
        <textarea value={form.text}
          onChange={e => { setForm(f => ({...f, text:e.target.value})); setErrors(er => ({...er, text:''})); }}
          placeholder="Share your experience at ANN'S…"
          className={`field-input field-textarea${errors.text ? ' error' : ''}`}
          rows={3} />
        {errors.text && <span className="field-err">{errors.text}</span>}
      </div>

      <button type="submit" className="btn-clay-submit" disabled={loading}>
        {loading ? <><span className="spinner" /> Posting…</> : 'Post My Review'}
      </button>
    </form>
  );
}
